import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getErrorMessage } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getSignedDocument } from '../services'
import { SignedDocument, SignedDocumentType } from '../types'

// Ký phiên thường trả `signed` ngay; hiếm khi còn `pending` thì hỏi lại mỗi 10s,
// tối đa ~3 phút (theo hướng dẫn backend). Mỗi lần hỏi backend đều gọi sang MISA.
const POLL_INTERVAL_MS = 10_000
const POLL_WINDOW_MS = 3 * 60_000

const NOT_SIGNED_YET = 'esign-request-not-found'

export const signedDocumentQueryKey = (
    type: SignedDocumentType,
    invoiceID: number
) => [QUERY_KEYS.INVOICE_SIGNED_DOCUMENT, type, invoiceID] as const

/**
 * Lần gửi ký mới nhất của một chứng từ ký số. `null` = chưa gửi ký lần nào.
 */
export const useSignedDocument = (
    type: SignedDocumentType,
    invoiceID: number
) => {
    return useQueryWithHandler<SignedDocument | null>(
        {
            queryKey: signedDocumentQueryKey(type, invoiceID),
            // App để staleTime mặc định 5 phút: trạng thái ký phải lấy mới mỗi lần mở popup
            // (vừa ký / ký lại ở tab khác, backend cũng tự chốt bản pending khi GET)
            staleTime: 0,
            queryFn: async () => {
                try {
                    const response = await getSignedDocument(type, invoiceID)
                    return response?.result ?? null
                } catch (error) {
                    if (getErrorMessage(error) === NOT_SIGNED_YET) return null
                    throw error
                }
            },
            refetchInterval: (query) => {
                const document = query.state.data
                if (document?.status !== 'pending') return false
                const elapsed =
                    Date.now() - new Date(document.created_at).getTime()
                return elapsed < POLL_WINDOW_MS ? POLL_INTERVAL_MS : false
            },
        },
        { showError: false }
    )
}
